import { cn, type VariantProps } from 'noddi-ui-common';
import { type CSSProperties, KeyboardEvent, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { cardVariants } from '../../base/cardVariants';

type NoddiCardWrapperProps = VariantProps<typeof cardVariants> & {
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  openInNewTab?: boolean;
  size?: 'default' | 'small';
  disableActiveScale?: boolean;
  title?: string;
  /**
   * Toggle state for cards that represent a selection: applies the selected fill and
   * reports `aria-pressed`. Omit for cards that just navigate or open something.
   */
  selected?: boolean;
  /**
   * Name for the card button when nested controls (Details, Add) would otherwise
   * become part of that name. Pass the same name and price the card renders.
   * The price comes from the caller on each render, so a backend change stays in the name.
   */
  accessibleLabel?: string;
  /** Keep embedded controls outside the card action's accessibility subtree. Requires `accessibleLabel`. */
  separateAction?: boolean;
} & ({ separateAction: true; accessibleLabel: string } | { separateAction?: false });

export const NoddiBasicCard = ({
  children,
  className,
  style,
  onClick,
  to,
  disabled,
  openInNewTab,
  size = 'default',
  disableActiveScale,
  variant,
  title,
  selected,
  accessibleLabel,
  separateAction
}: PropsWithChildren<NoddiCardWrapperProps>) => {
  const cardClasses = cn(
    cardVariants({ variant }),
    // Kept out of `cardVariants` on purpose: the generic Card / NoddiCard wrappers share its
    // VariantProps but have no selection semantics, so a variant there would let them advertise
    // a `selected` prop that does nothing.
    selected && 'bg-card-selected-background text-card-selected-foreground',
    'text-start',
    size === 'small' && 'px-3 py-2',
    (onClick || to) &&
      cn(
        'transition-transform focus-visible:ring-2 focus-visible:ring-grey focus-visible:outline-hidden md:hover:shadow-xl',
        // Apply active scale unless disabled or a nested interactive element opts out via [data-prevent-card-bounce]
        !disableActiveScale && '[&:not(:has([data-prevent-card-bounce]:active))]:active:scale-95'
      ),
    className
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Keydown bubbles from nested buttons. Enter on Details must not also toggle the card.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (!onClick || disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      if (event.key === ' ') {
        event.preventDefault();
      }
      onClick();
    }
  };

  // Link (anchor tag) is only one with to prop, so render Link. ( It also has optional onClick prop)
  if (to) {
    return (
      <Link
        to={to}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noreferrer' : undefined}
        onClick={onClick}
        className={cardClasses}
        style={style}
        title={title}
        aria-label={accessibleLabel}
      >
        {children}
      </Link>
    );
  }

  if (separateAction && accessibleLabel) {
    // Stays mounted while `onClick` is briefly absent (loading), so a focused card keeps focus.
    const inactive = disabled || !onClick;
    return (
      <div className={cn(cardClasses, 'relative')} style={style} title={title}>
        <button
          type='button'
          className='absolute inset-0 rounded-[inherit] focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-hidden'
          onClick={inactive ? undefined : onClick}
          aria-disabled={inactive || undefined}
          aria-label={accessibleLabel}
          aria-pressed={selected}
        />
        {/* The surface still selects the card. Real controls stay clickable; an aria-hidden indicator does not swallow the click. */}
        <div className='pointer-events-none contents [&_a]:pointer-events-auto [&_button:not([aria-hidden])]:pointer-events-auto [&_input]:pointer-events-auto [&>*]:relative'>
          {children}
        </div>
      </div>
    );
  }

  // Plain card actions retain the wrapper; cards with embedded controls use a sibling action above.
  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- the role is conditional (`onClick ? 'button' : undefined`), which the rule cannot evaluate; the card already sets onKeyDown and tabIndex
    <div
      onClick={disabled ? undefined : onClick}
      role={onClick ? 'button' : undefined}
      onKeyDown={handleKeyDown}
      // A disabled card leaves the tab order. It has no separate reason to announce,
      // and the separate-action button is the path that stays focusable while unavailable.
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-disabled={onClick && disabled ? true : undefined}
      aria-label={accessibleLabel}
      // Only cards that model a selection are toggle buttons. Reporting
      // aria-pressed="false" on every clickable card told screen readers that a
      // selected card was unselected.
      aria-pressed={onClick && selected !== undefined ? selected : undefined}
      className={cardClasses}
      style={style}
      title={title}
    >
      {children}
    </div>
  );
};
